import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';
import { Button, TextField, CircularProgress, Box, Backdrop, Modal, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import lmsIntegrationAgent from '../../services/api/lmsIntegrationAgent';
import { ELMSType, IWPLinkRequest } from '../../services/models/LMSLinkModel';
import SnackbarUtils from '../../components/snackbarUtils/SnackbarUtilsConfigurator';

interface IntegrationComponentProps {
  ownerID: number;
  loadingOwnerID: boolean;
}

export default function NewWPIntegration({ ownerID, loadingOwnerID }: IntegrationComponentProps) {
  const theme = useTheme();
  const history = useHistory();
  const [queryParams] = useQueryParams({
    site_url: StringParam,
    user_login: StringParam,
    password: StringParam,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);

  const decodedUserLogin = queryParams.user_login ? decodeURIComponent(queryParams.user_login) : null;
  const decodedPassword = queryParams.password ? decodeURIComponent(queryParams.password) : null;
  const decodedSiteUrl = queryParams.site_url ? decodeURIComponent(queryParams.site_url) : null;

  const [websiteUrl, setWebsiteUrl] = useState('');
  const currentOrigin = window.location.protocol + '//' + window.location.host;
  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWebsiteUrl(event.target.value.replace(/\/$/, ''));
  };

  // Show processing modal when we have WordPress credentials
  useEffect(() => {
    if (decodedUserLogin && decodedPassword && decodedSiteUrl) {
      setShowProcessingModal(true);
    }
  }, [decodedUserLogin, decodedPassword, decodedSiteUrl]);

  // Handle submission when we have both owner ID and URL params
  useEffect(() => {
    if (decodedUserLogin && decodedPassword && decodedSiteUrl && ownerID && !loadingOwnerID && !isSubmitting && !hasSubmitted) {
      handleWPLinkSubmission();
    }
  }, [decodedUserLogin, decodedPassword, decodedSiteUrl, ownerID, loadingOwnerID, isSubmitting, hasSubmitted]);

  const handleWPLinkSubmission = async () => {
    if (!ownerID || !decodedSiteUrl || !decodedUserLogin || !decodedPassword) {
      return;
    }

    setIsSubmitting(true);
    setHasSubmitted(true);

    try {
      const wpLinkData: IWPLinkRequest = {
        identifier: decodedSiteUrl,
        username: decodedUserLogin,
        app_password: decodedPassword,
        creatorOwnerId: ownerID,
        lmsTypeID: ELMSType.WordPress,
      };

      const response = await lmsIntegrationAgent.CourseLMSLink.AddLMSIntegration(wpLinkData);

      if (response.success === true || response.success === undefined || response.id) {
        SnackbarUtils.success('WordPress link added/updated successfully');
        // Small delay to show the success message in modal, then redirect
        setTimeout(() => {
          setShowProcessingModal(false);
          history.push('/integrations?tab=Active%20Integrations');
        }, 2000);
      } else {
        SnackbarUtils.error(response.message || 'Failed to add WordPress integration');
        setIsSubmitting(false);
        setHasSubmitted(false); // Allow retry on error
        setShowProcessingModal(false);
      }
    } catch (error: any) {
      console.error('Error adding WP Link:', error);
      SnackbarUtils.error(error.response?.data?.message || 'Failed to add WordPress integration');
      setIsSubmitting(false);
      setHasSubmitted(false); // Allow retry on error
      setShowProcessingModal(false);
    }
  };

  return (
    <>
      {!(decodedUserLogin && decodedPassword) ? (
        <>
          <TextField label={'Wordpress URL'} variant='outlined' value={websiteUrl} onChange={handleUrlChange} placeholder={'https://your-website.com'} />

          <Button
            variant='contained'
            color='primary'
            component='a'
            style={{ marginTop: theme.spacing(1) }}
            href={`${websiteUrl}/wp-admin/authorize-application.php?app_name=mentorkit&success_url=${currentOrigin}/integrations?tab=Add%20New%20Integration`}
            // target='_blank'
            rel='noopener noreferrer'
            disabled={!websiteUrl.trim()}
          >
            {'Authorize WordPress'}
          </Button>
        </>
      ) : (
        <div style={{ marginTop: theme.spacing(3), padding: theme.spacing(2), background: theme.palette.background.paper, borderRadius: theme.spacing(1) }}>
          <Typography variant='h6' gutterBottom>
            WordPress Credentials Received:
          </Typography>
          <Typography variant='body1'>
            <strong>Site URL:</strong> {decodedSiteUrl}
          </Typography>
          <Typography variant='body1'>
            <strong>Username:</strong> {decodedUserLogin}
          </Typography>
          <Typography variant='body1'>
            <strong>Password:</strong> {'•'.repeat(decodedPassword.length)}
          </Typography>
          <Typography variant='body2' color='textSecondary' style={{ marginTop: theme.spacing(1) }}>
            Processing your integration...
          </Typography>
        </div>
      )}

      {/* Processing Modal */}
      <Modal
        open={showProcessingModal}
        onClose={() => {}} // Prevent closing by clicking outside
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            outline: 'none',
            textAlign: 'center',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {!hasSubmitted ? (
            <>
              <CircularProgress size={60} sx={{ mb: 3, color: theme.palette.primary.main }} />
              <Typography variant='h5' gutterBottom sx={{ color: theme.palette.text.primary }}>
                Setting up your integration
              </Typography>
              <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
                Wrapping things up...
              </Typography>
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ fontSize: 60, color: theme.palette.success?.main || '#4caf50', mb: 3 }} />
              <Typography variant='h5' gutterBottom sx={{ color: theme.palette.success?.main || '#4caf50' }}>
                Integration Complete!
              </Typography>
              <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
                Your WordPress site has been successfully connected. Redirecting you now...
              </Typography>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
}
